import React from 'react';
import {Box, Pagination, Typography} from '@mui/material';
import {useListContext} from 'react-admin';
import {t} from '../../themes/statamicTokens';
import PageHeader from './PageHeader';
import SavedViewsTabs from './SavedViewsTabs';
import FilterBar from './FilterBar';
import StatamicTable from './StatamicTable';
import ScopeBanner from './ScopeBanner';
import useScopedFilter from '../../scope/useScopedFilter';

const StatamicListPagination = () => {
    const {page, perPage, total, setPage} = useListContext();
    const pageCount = Math.max(1, Math.ceil((total ?? 0) / (perPage ?? 25)));
    if (pageCount <= 1) return null;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 3,
                py: 1.5,
                borderTop: `1px solid ${t.color.border.hairline}`,
            }}
        >
            <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.muted}}>
                Showing page {page} of {pageCount} · {total ?? 0} total
            </Typography>
            <Pagination
                count={pageCount}
                page={page}
                onChange={(_, p) => setPage(p)}
                size="small"
                shape="rounded"
                sx={{
                    '& .MuiPaginationItem-root': {
                        fontSize: `${t.type.caption.size}px`,
                        color: t.color.text.secondary,
                        borderColor: t.color.border.default,
                    },
                    '& .Mui-selected': {
                        backgroundColor: `${t.color.accent.primaryTint} !important`,
                        color: `${t.color.accent.primary} !important`,
                    },
                }}
            />
        </Box>
    );
};

/**
 * Composes the standard Statamic list chrome inside a ListContext.
 * Wrap with `<List actions={false} pagination={false} component={Box}>` at the call site.
 *
 * Owns: outer page background, PageHeader, surface card, optional SavedViewsTabs,
 * optional FilterBar, StatamicTable, and pagination. No state — all control props
 * come from the caller.
 */
const StatamicListPage = ({
    title,
    subtitle,
    headerActions,
    views,
    activeView,
    onViewSelect,
    onViewAdd,
    searchPlaceholder,
    onSearchChange,
    filters,
    columns,
    rowActions,
    onRowClick,
    scoped,
    children,
}) => {
    const ctx = useListContext();
    const {filterValues, setFilters, displayedFilters} = ctx;

    useScopedFilter(scoped);

    const handleSearchChange = onSearchChange
        ?? ((q) => setFilters({...filterValues, q}, displayedFilters));

    const showViews = Array.isArray(views) && views.length > 0;
    const showFilterBar = Boolean(filters) || Boolean(searchPlaceholder);

    return (
        <Box sx={{backgroundColor: t.color.page, minHeight: '100%'}}>
            <PageHeader title={title} subtitle={subtitle} actions={headerActions}/>

            <Box
                sx={{
                    backgroundColor: t.color.surface,
                    border: `1px solid ${t.color.border.default}`,
                    borderRadius: `${t.radius.md}px`,
                    mx: 3,
                    mb: 3,
                    overflow: 'hidden',
                }}
            >
                {scoped && <ScopeBanner/>}

                {showViews && (
                    <SavedViewsTabs
                        views={views}
                        activeId={activeView}
                        onSelect={onViewSelect}
                        onAdd={onViewAdd ?? (() => {})}
                    />
                )}

                {showFilterBar && (
                    <FilterBar
                        searchValue={filterValues?.q}
                        onSearchChange={handleSearchChange}
                        placeholder={searchPlaceholder}
                        filters={filters}
                    />
                )}

                <StatamicTable
                    columns={columns}
                    rowActions={rowActions}
                    onRowClick={onRowClick}
                />

                <StatamicListPagination/>
            </Box>

            {children}
        </Box>
    );
};

export default StatamicListPage;
